import {createPlayerState, createTrack, Status, createCastState} from "./interfaces";
import {Fullscreen} from "./fullscreen";

// If iOS returns true
// If iPadOS:
// * If Safari returns false
// * If Chrome (or Firefox) returns true (fullscreen API isn't available)
const iOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;

// We do not want to create a dependency here to clpp, since this might be loaded
// via module or via global namespace etc. Instead the mixins module must be
// initialized to fully function
let clpp = null;

export function initMixins(clppNamespace) {
  clpp = clppNamespace
}

export const PPComponentMixin = {
  inject: ['player'],
  props: {
    chromecast: {
      type: Object,
    },
    airplay: {
      type: Object,
    },
    applyStyles: {
      type: Boolean,
      default: true
    },
  },
};


export const PPLanguageFilters = {
  filters: {
    language: function (value) {
      switch (value) {
        case 'zh':
        case 'ch':
        case 'chi':
          return 'Chinese';
        case 'en':
        case 'eng':
          return 'English';
        case 'it':
        case 'ita':
          return 'Italian';
        case 'de':
        case 'ger':
          return 'German';
        case 'ru':
        case 'rus':
          return 'Russian';
        case 'pt':
        case 'por':
          return 'Portuguese';
        case 'fr':
        case 'fre':
          return 'French';
        case 'es':
        case 'spa':
          return 'Spanish';
        case 'nl':
        case 'dut':
          return 'Dutch';
        case 'ja':
        case 'jap':
          return 'Japanese';
        case 'fa':
        case 'fas':
        case 'per':
          return 'Persian';
        case 'und':
          return 'Undefined';
      }
      return value;
    },
    roles: function (value) {
      let role = value && value[0] || '';
      return role.toLowerCase() === 'caption' ? 'Caption' : 'Subtitle';
    },
    kind: function (value, braces = false) {
      const kind = value || '';
      let result = '';

      switch (kind.toLowerCase()) {
        case 'caption':
          result = 'CC';
          break;
        case 'forced':
          result = 'FORCED';
          break;
        default:
          result = '';
      }

      return braces && result ? '[' + result + ']' : result;
    }
  }
};

function startsWith(string, search, rawPos) {
  if (!string) return false;
  if (String.prototype.startsWith) return string.startsWith(search, rawPos);
  let pos = rawPos > 0 ? rawPos | 0 : 0;
  return this.substring(pos, pos + search.length) === search;
}

export const PPCodecFilters = {
  filters: {
    channels: function (value) {
      switch (value) {
        case 1: return 'Mono';
        case 2: return 'Stereo';
        case 6: return '5.1';
        case 8: return '7.1';
      }
      return value
    },
    codec: function (value) {
      if(startsWith(value, 'mp4a.40.29')) return 'HE-AAC';
      if(startsWith(value, 'mp4a.40.2')) return 'LC-AAC';
      if(startsWith(value, 'mp4a.40')) return 'AAC';
      if(startsWith(value, 'mp4a.A5')) return 'Dolby';
      if(startsWith(value, 'mp4a.A6')) return 'Dolby+';
      if(startsWith(value, 'dvav')) return 'Dolby Vision';
      if(startsWith(value, 'dvhe')) return 'Dolby Vision';
      if(startsWith(value, 'dts')) return 'DTS';
      if(startsWith(value, 'text/vtt')) return 'WebVTT';
      if(startsWith(value, 'application/ttml+xml')) return 'TTML';
      return value;
    },
    resolution: function (value) {
      return `${value.width}x${value.height}`;
    },
    bitrate: function (value) {
      let bps = value.bandwidth;
      if(bps < 1000) return `${bps} bps`;
      if(bps < 1000000) return `${(bps / 1000).toFixed(0)} Kbps`;
      return `${(bps / 1000000).toFixed(2)} Mbps`;
    }
  }
};


function initWebPlayer(video, uiState, config) {
  const player = new clpp.Player(video, config);
  let currentPeriod = null;
  player.on(clpp.events.ADS_TIMELINE_CHANGED, (e) => {
    uiState.ad.cuePoints = e.adsTimeline.getCuePoints();
  });
  player.on('timeupdate', () => {
    uiState.currentTime = player.getPosition();
    uiState.targetPosition = -1;
    uiState.seekRange = player.getSeekRange();
    if (currentPeriod) {
      uiState.periodDuration = currentPeriod.duration;
      uiState.periodTime = uiState.currentTime - currentPeriod.startTime;
    }
  });
  player.on('volumechange', () => {
    uiState.volume = player.getVolume();
    uiState.muted = player.isMuted();
  });
  player.on('durationchange', () => {
    uiState.seekRange = player.getSeekRange();
    if(player.isLive()) {
      uiState.duration = player.getSeekRange().end;
    } else {
      uiState.duration = player.getDuration();
    }
  });
  player.on('ad-started', event => {
    uiState.ad.active = true;
    uiState.ad.playing = true;
    uiState.ad.duration = event.ad.getDuration();
    uiState.ad.muted = player.getAdsManager().getVolume() === 0;
    uiState.ad.positionInSequence = event.ad.getPositionInSequence();
    uiState.ad.sequenceLength = event.ad.getSequenceLength();
    uiState.ad.title = event.ad.getTitle();
  });
  player.on('ad-progress', () => {
    uiState.ad.loading = false;
    uiState.ad.currentTime = player.getAdsManager().getPosition();
  });
  player.on('ad-buffering', () => {
    uiState.ad.loading = true;
  });
  player.on('ad-resumed', () => {
    uiState.ad.playing = true;
  });
  player.on('ad-paused', () => {
    uiState.ad.playing = false;
  });
  player.on('ad-break-stopped', () => {
    uiState.ad.active = false;
  });
  player.on('periodchanged', (e) => {
    currentPeriod = e.details;
    uiState.periodDuration = currentPeriod.duration;
    uiState.periodTime = player.getPosition() - currentPeriod.startTime;
  });

  // TODO: Looks like in shaka this is handled differently
  // an the reported rate is not changed when we receive this
  // event so we can not listen for changes here. Shaka also changes
  // the rate for internal purposes, so maybe we need to change how/when we
  // trigger this event or we need a separate event ?
  // player.on('ratechange', () => {
  //   this.player.playbackRate = player.getPlaybackRate();
  //   console.log(">>> RATE", this.player.playbackRate);
  // });

  player.on('tracksadded', () => {
    let toTrack = (d) => createTrack(Object.assign({}, d, {
      bitrate: d.bandwidth,
      channels: d.channelsCount
    }));

    const trackManager = player.getTrackManager();

    uiState.videoTracks = trackManager.getVideoTracks()
        .filter(t => !t.trickPlay)
        .flatMap(t => t.renditions)
        .sort((a,b) => a.bandwidth - b.bandwidth)
        .map(toTrack);
    uiState.canSelectVideoTracks = trackManager.canSelectVideoTracks() && uiState.videoTracks.length > 1;
    uiState.audioTracks = trackManager.getAudioTracks().flatMap(t => t.renditions).map(toTrack);
    uiState.textTracks = trackManager.getTextTracks().flatMap((t) => t.renditions).map(toTrack);

    let activeVideo = trackManager.getVideoRendition();
    let activeAudio = trackManager.getAudioRendition();
    let activeText = trackManager.getTextRendition();

    uiState.activeTracks.video = activeVideo ? activeVideo : null;
    uiState.activeTracks.audio = activeAudio ? activeAudio : null;
    uiState.activeTracks.text = activeText ? activeText : null;
    uiState.activeTracks.abr = trackManager.isAbrEnabled();
  });

  player.on('audiotrackchanged', () => {
    let active = player.getTrackManager().getAudioRendition();
    uiState.activeTracks.audio = active ? active : null;
  });
  player.on('videotrackchanged', () => {
    const trackManager = player.getTrackManager();
    let active = trackManager.getVideoRendition();
    uiState.activeTracks.video = active ? active : null;
    uiState.activeTracks.abr = trackManager.isAbrEnabled();
  });
  player.on('bitratechanged', (e) => {
    const trackManager = player.getTrackManager();
    let active = trackManager.getVideoRendition();
    if(active != null && active.width !== null && active.height !== null) {
      uiState.activeTracks.video = active ? active : null;
    } else {
      uiState.activeTracks.video = {
        width: e.detail.width,
        height: e.detail.height
      }
    }
    uiState.activeTracks.abr = trackManager.isAbrEnabled();
  });
  player.on('texttrackchanged', () => {
    let active = player.getTrackManager().getTextRendition();
    uiState.activeTracks.text = active ? active : null;
  });

  player.on('statechanged', () => {
    uiState.currentTime = player.getPosition();
    uiState.seekRange = player.getSeekRange();
    // since these (especially muted) might be preserved from the
    // element, we should verify them on state change so when a new
    // item is loaded they are up-to-date
    uiState.volume = player.getVolume();
    uiState.muted = player.isMuted();

    if(player.isLive()) {
      uiState.duration = player.getSeekRange().end;
    } else {
      uiState.duration = player.getDuration();
    }

    const clppState = player.getState();
    switch (clppState) {
      case clpp.Player.State.IDLE:
        uiState.playingAd = false;
        uiState.status = Status.IDLE;
        uiState.loading = false;
        uiState.ad.cuePoints = []
        uiState.ad.active = false;
        uiState.ad.loading = false;
        uiState.ad.playing = false;
        uiState.ad.muted = false;
        uiState.ad.currentTime = 0;
        uiState.ad.duration = 0;
        uiState.ad.positionInSequence = 1;
        uiState.ad.sequenceLength = 1;
        uiState.ad.title = '';

        break;
      case clpp.Player.State.BUFFERING:
        uiState.status = Status.BUFFERING;
        uiState.loading = true;
        break;
      case clpp.Player.State.PREPARING:
        uiState.status = Status.PREPARING;
        uiState.loading = true;
        break;
      case clpp.Player.State.ENDED:
        uiState.status = Status.ENDED;
        uiState.playing = false;
        uiState.loading = false;
        break;
      case clpp.Player.State.PAUSED:
        uiState.status = Status.PAUSED;
        uiState.playing = false;
        uiState.loading = false;
        break;
      case clpp.Player.State.PLAYING:
        uiState.status = Status.PLAYING;
        uiState.playing = true;
        uiState.loading = false;
        break;
      case clpp.Player.State.ERROR:
        uiState.status = Status.ERROR;
        uiState.loading = false;
        break;
    }
  });
  return player;
}

function isCurrentTrack (current, value) {
  if (current && value) {
    if (current.id === value.id) {
      // the track is already selected
      return true;
    }
  } else if(!current && !value) {
    return true;
  }
  return false;
}

/**
 * A mixin adding player state.
 */
export const PPPlayerWebMixin = {
  data() {
    return {
      player: createPlayerState(),
      ppContainer: null,
      ppPlayerRef: new PPWeakReference(),
    }
  },
  provide() {
    return {
      player: this.player,
      ppPlayerRef: this.ppPlayerRef,
    }
  },
  watch: {
    'player.targetPosition': function (value) {
      if (value >= 0 && Number.isFinite(value)) {
        this.ppPlayerRef.get().seek(value);
      }
    },
    'player.fullscreen': function (value) {
      if (value) {
        this.fullscreen.requestFullscreen();
      } else if(this.fullscreen.fullscreenEnabled()){
        this.fullscreen.exitFullscreen();
      }
    },
    'player.volume': function (value) {
      this.ppPlayerRef.get().setVolume(value);
    },
    'player.playbackRate': function (value) {
      this.ppPlayerRef.get().setPlaybackRate(value);
    },
    'player.activeTracks.video': function (value) {
      const current  = this.ppPlayerRef.get().getTrackManager().getVideoRendition();
      if (isCurrentTrack(current, value)) {
        // the track is already selected
        return;
      }
      if(value !== null) {
        let r = this.ppPlayerRef.get().getTrackManager().findVideoRendition({ id: value.id });
        this.ppPlayerRef.get().getTrackManager().setVideoRendition(r, true);
      } else {
        this.ppPlayerRef.get().getTrackManager().setVideoRendition(null);
      }
    },
    'player.activeTracks.audio': function (value) {
      if (value && value.codecs !== null) {
        const current  = this.ppPlayerRef.get().getTrackManager().getAudioRendition();
        if (isCurrentTrack(current, value)) {
          // the track is already selected
          return;
        }

        let r = this.ppPlayerRef.get().getTrackManager().findAudioRendition({ id: value.id });
        this.ppPlayerRef.get().getTrackManager().setAudioRendition(r);
      }
    },
    'player.activeTracks.text': function (value) {
      const current  = this.ppPlayerRef.get().getTrackManager().getTextRendition();
      if (isCurrentTrack(current, value)) {
        // the track is already selected
        return;
      }
      let r = value !== null ? this.ppPlayerRef.get().getTrackManager().findTextRendition({ id: value.id }) : null;
      this.ppPlayerRef.get().getTrackManager().setTextRendition(r);
    },
    'player.hoverPosition': function (value) {
      // we are watching the hover position here to update the
      // thumbnails in the player interface. This is located here
      // so we can handle the logic in the interface binding instead
      // of inside a thumb component
      let thumbsPlugin = this.ppPlayerRef.get().getPlugin('thumbnails');
      if (!thumbsPlugin) {
        return;
      }

      if(value >= 0) {
        thumbsPlugin.get(value).then(t => {
          if (this.player.hoverPosition < 0) return;

          this.player.thumb.current = t;
          this.player.thumb.position = value;
          let seekRange = this.player.seekRange;
          let range = seekRange.end - seekRange.start;
          value = value - seekRange.start;
          this.player.thumb.positionRelative = value / range;
        }).catch(() => {
          this.player.thumb.current = null;
          this.player.thumb.position = 0;
          this.player.thumb.positionRelative = 0;
        });
      } else {
        this.player.thumb.current = null;
        this.player.thumb.position = 0;
        this.player.thumb.positionRelative = 0;
      }
    }
  },
  methods:  {
    initPlayer(video, container, config) {
      this.ppContainer = container;

      let fullscreenElement, fullscreenDocument;
      if (iOS) {
        fullscreenElement = video;
        fullscreenDocument = video;
      } else {
        fullscreenElement = this.ppContainer;
        fullscreenDocument = document;
      }

      this.fullscreen = new Fullscreen(fullscreenElement, fullscreenDocument, () => {
        if (!this.fullscreen.fullscreenEnabled()) {
          this.player.fullscreen = false;
        }
      });

      this.ppPlayerRef.set(initWebPlayer(video, this.player, config));
      return this.ppPlayerRef.get();
    }
  },
  destroyed() {
    if (this.ppPlayerRef) {
      this.ppPlayerRef.get().destroy();
    }
  }
};

/**
 * A mixin adding player methods.
 * This mixin can be used multiple times.
 */
export const PPPlayerMethodsMixin = {
  inject: ['ppPlayerRef', 'player'],
  methods:  {
    /**
     * Pause playback of content or ad.
     */
    pause() {
      if (this.player.ad.active) {
        if (!this.player.ad.playing) return;
        const adsManager = this.ppPlayerRef.get().getAdsManager();
        if (adsManager == null) return;
        adsManager.pause();
        this.player.ad.playing = false;
        return;
      }

      if (!this.player.playing) return;
      this.ppPlayerRef.get().pause();
      this.player.playing = false;
    },
    /**
     * Resume playback of content or ad.
     */
    play() {
      if (this.player.ad.active) {
        if (this.player.ad.playing) return;
        const adsManager = this.ppPlayerRef.get().getAdsManager();
        if (adsManager == null) return;
        adsManager.resume();
        this.player.ad.playing = true;
        return;
      }

      if (this.player.playing) return;
      this.ppPlayerRef.get().play();
      this.player.playing = true;
    },
    /**
     * Toggle play/pause of playback of content or ad.
     */
    togglePlayPause() {
      const isPlaying = this.player.ad.active ?
        this.player.ad.playing : this.player.playing;
      if (isPlaying) {
        this.pause();
      } else {
        this.play();
      }
    },
    /**
     * Toggle volume mute of content or ad.
     */
    toggleMuted() {
      if (this.player.ad.active) {
        const adsManager = this.ppPlayerRef.get().getAdsManager();
        if (!adsManager) return null;
        adsManager.setVolume(this.player.ad.muted ? 1 : 0);
        this.player.ad.muted = !this.player.ad.muted;
        return;
      }

      this.ppPlayerRef.get().setMuted(!this.player.muted);
      this.player.muted = !this.player.muted;
    },
  },
};

/**
 * Same as PPPlayerMethodsMixin but this mixin must be used in a component
 * which defines 'ppPlayerRef' and 'player'.
 */
export const PPPlayerMethodsMixinRoot = {
  ...PPPlayerMethodsMixin,
  inject: [],
}

export const PPCastProxyMixin = {
  data() {
    return {
      chromecast: createCastState(),
      ppCastRef: null,
      castListener: null,
      errorListener: null
    }
  },
  mounted() {
    this.castListener = () => {
      this.chromecast.canCast = this.ppCastRef.get().canCast();
      this.chromecast.isCasting = this.ppCastRef.get().isCasting();
      this.chromecast.receiverName = this.ppCastRef.get().getReceiverName();
      this.chromecast.startCasting = false;
      this.chromecast.stopCasting = false;
    };
    this.errorListener = () => {
      // In case of error, ensure to enable chromecast button
      this.chromecast.startCasting = false;
      this.chromecast.stopCasting = false;
    };
  },
  destroyed() {
    if (this.ppCastRef) {
      this.ppCastRef.get().off(clpp.events.CAST_STATUS_CHANGED, this.castListener);
      this.ppCastRef.get().off(clpp.events.ERROR, this.errorListener);
      this.castListener = null;
      this.errorListener = null;
      this.ppCastRef.get().destroy();
      this.ppCastRef = null;
    }
  },
  methods: {
    initCastProxy(player, receiverAppId) {
      this.ppCastRef = new PPWeakReference();
      const castProxy = new clpp.cast.CastProxy(player, receiverAppId);
      castProxy.on(clpp.events.CAST_STATUS_CHANGED, this.castListener);
      castProxy.on(clpp.events.ERROR, this.errorListener);
      this.ppCastRef.set(castProxy);
      return this.ppCastRef.get();
    },
  },
  watch: {
    'chromecast.startCasting': function (value) {
      if (value) {
        this.ppCastRef.get().cast();
      }
    },
    'chromecast.stopCasting': function (value) {
      if (value) {
        this.ppCastRef.get().forceDisconnect();
      }
    },
  }
};

/**
 * A mixin adding keyboard shortcuts/control for the player.
 * This mixin must be used along with PPPlayerMethodsMixin.
 */
export const PPPKeyboardMixin = {
  data() {
    return {
      ppKeyListener: null
    }
  },
  mounted() {
    this.keyListener = (e) => {
      let active = document.activeElement;
      if (active !== null && active !== document.body) {
        return;
      }

      if (this.player.ad.active) {
        return;
      }

      if(e.key === ' ') {
        this.togglePlayPause();
        e.preventDefault();
      } else if( e.key === 'ArrowRight' && !this.player.playingAd) {
        let t = this.player.targetPosition > 0 ? this.player.targetPosition : this.player.currentTime;
        this.player.targetPosition = t + 10;
        // TODO: This was canceling hiding of the UI
        // this.onmouseover();
        e.preventDefault();
      } else if( e.key === 'ArrowLeft' && !this.player.playingAd) {
        let t = this.player.targetPosition > 0 ? this.player.targetPosition : this.player.currentTime;
        this.player.targetPosition = t - 10;
        // this.onmouseover();
        // TODO: This was canceling hiding of the UI
        e.preventDefault();
      }
    };
    window.addEventListener("keydown", this.keyListener);
  },
  destroyed() {
    if (this.keyListener) {
      window.removeEventListener("keydown", this.keyListener);
    }
  }
};

export const PPAirPlayMixin = {
  data() {
    return {
      airplay: {
        canCast: false,
        casting: false,
        showCastMenu: () => {}
      },
      ppAirPlayRef: null,
    }
  },
  methods: {
    initAirPlay () {
      // console.assert(this.ppPlayerRef, 'The player instance not available.');
      this.toggleListeners('on');
      this.airplay.showCastMenu = () => {
        const plugin = this.ppPlayerRef.get().getPlugin('airplay');
        // Safari should show native OSD when casting.
        plugin.showCastMenu();
      }
      const plugin = this.ppPlayerRef.get().getPlugin('airplay');
      if (plugin) {
        this.airplay.canCast = plugin.canCast()
        this.airplay.casting = plugin.isCasting()
      }
    },
    toggleListeners (action) {
      const ppPlayer = this.ppPlayerRef.get();

      ppPlayer[action](
        clpp.events.AIRPLAY_STATUS_CHANGED,
        this.onAvailabilityChanged
      );
      ppPlayer[action](
        clpp.events.AIRPLAY_CASTING_STARTED,
        this.onCastingStarted
      );
      ppPlayer[action](
        clpp.events.AIRPLAY_CASTING_ENDED,
        this.onCastingEnded
      );
    },
    onAvailabilityChanged (event) {
      this.airplay.canCast = event.canCast;
    },
    onCastingStarted () {
      this.airplay.casting = true;
    },
    onCastingEnded () {
      this.airplay.casting = false;
    }
  },
  destroyed() {
    this.toggleListeners('off');
  }
};


/*
 * The weak reference object prevents Vue from observing the internals.
 *
 * When you pass a object to a Vue instance as its data option,
 * Vue will walk through all of its properties and convert them to getter/setters:
 *
 * https://vuejs.org/v2/guide/reactivity.html
 *
 * Vue iterates over all properties in a graph (including nested properties).
 * If the object isn't a plain Javascript object, then this might cause
 * unexpected behaviour or failures.
 *
 * Problematic conversion is done when updating a data option.
 * When the data option is updated one can create a child property
 * with the `set` method. The child property isn't observed by Vue.
 *
 * Finally, one can access the child property using the `get` method.
 *
 * For example:
 *
 * data: { ppPlayerRef: null }
 *
 * [...]
 *
 * this.ppPlayerRef = new PPWeakReference();
 * this.ppPlayerRef.set(player);
 *
 * [...]
 *
 * this.ppPlayerRef.get().play();
 */
export class PPWeakReference {
  set (ref) {
    if (typeof ref !== 'object') {
      throw new Error(
        'The weak reference accepts object type exclusively.'
      );
    }

    if (this.ref_) {
      throw new Error(
        'The weak reference can not be assigned multiple times.'
      );
    }

    this.ref_ = ref;
  }

  get () {
    if (!this.ref_) {
      throw new Error(
        'The weak reference has not been initialized.'
      );
    }

    return this.ref_;
  }
}
